import { yupResolver } from "@hookform/resolvers/yup";
import { Close as CloseIcon } from "@mui/icons-material";
import {
    Button,
    Card,
    CardContent,
    GridLegacy as Grid,
    IconButton,
    Link,
    Menu,
    MenuItem,
    Stack,
    Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { flow, isEmpty, sum } from "lodash-es";
import React, { ReactNode, useEffect, useState } from "react";
import { FormProvider, SubmitHandler, useFieldArray, useForm, useFormContext } from "react-hook-form";
import * as yup from "yup";

import { useMenuState } from "../../hooks/useMenuState";
import { EVENTS, Segment } from "../../services/segment";
import { isDayjsBeforeToday } from "../../utils/dayjs/isDayjsBeforeToday";
import { isValidDateOfBirth } from "../../utils/dayjs/isValidDateOfBirth";
import { preventFormSubmitOnEnter } from "../../utils/formUtils";
import { onlyContainsNumbers, yupValidatePostalCode, yupValidateState } from "../../utils/validationUtils";
import { fullNameRequired, noNumbersAllowed } from "../../utils/validators";
import { ControllerCountrySelect } from "../form/ControllerCountrySelect";
import { FormCheckbox } from "../form/FormCheckbox";
import { FormDatePicker } from "../form/FormDatePicker";
import { FormInputWrapper } from "../form/FormInputWrapper";
import { FormPhoneInput, isPhoneNumberPossible } from "../form/FormPhoneInput";
import { FormSpreadAddressInput } from "../form/FormSpreadAddressInput";
import { FormTextInput } from "../form/FormTextInput";
import { SSN_REGEX } from "./constants";
import { BeneficialOwner, IBeneficialOwnersFormData, IOfficerInformationFormData } from "./FormData";
import { usePassportCountryOptions } from "./usePassportCountryOptions";

const NEW_BENEFICIAL_OWNER: BeneficialOwner = {
    firstName: "",
    lastName: "",
    dob: undefined,
    ssn: "",
    usePassport: false,
    passportNumber: "",
    nationality: "",
    email: "",
    percentage: undefined,
    phoneNumberFull: "",
    phoneCountryCode: "",
    phoneNumber: "",
    addressStreet: "",
    addressStreet2: "",
    addressCity: "",
    addressDistrict: "",
    addressZipcode: "",
    addressCountry: "",
};

export const formSchema = yup.object({
    beneficialOwner: yup
        .array()
        .of(
            yup
                .object({
                    firstName: yup
                        .string()
                        .required()
                        .test(fullNameRequired)
                        .test(noNumbersAllowed)
                        .label("First Name"),
                    lastName: yup.string().required().test(fullNameRequired).test(noNumbersAllowed).label("Last Name"),
                    dob: yup
                        .date()
                        .required()
                        .label("Date of Birth")
                        .test({
                            message: "Date of Birth must be in the past.",
                            test: (value) => isDayjsBeforeToday(dayjs(value)),
                        })
                        .test({
                            message: "Date of Birth must be valid.",
                            test: (value) => !isValidDateOfBirth(dayjs(value)),
                        }),
                    usePassport: yup.bool().required(),
                    ssn: yup
                        .string()
                        .when("usePassport", {
                            is: false,
                            then: (schema) => schema.defined().required().matches(SSN_REGEX, "SSN is invalid"),
                            otherwise: (schema) => schema.nullable(),
                        })
                        .label("SSN"),
                    passportNumber: yup
                        .string()
                        .when("usePassport", {
                            is: true,
                            then: (schema) => schema.required(),
                        })
                        .label("Passport Number"),
                    nationality: yup
                        .string()
                        .when("usePassport", {
                            is: true,
                            then: (schema) => schema.required("Nationality is required"),
                        })
                        .label("Nationality"),
                    email: yup.string().required().email("Email address is invalid").label("Email"),
                    percentage: yup
                        .number()
                        .typeError("Ownership Percentage must be a number")
                        .required()
                        .min(25, "Must have at least 25% ownership")
                        .max(100, "Cannot have more than 100% ownership")
                        .label("Ownership Percentage"),
                    phoneNumberFull: yup
                        .string()
                        .test({
                            message: "Phone Number is required",
                            test: (_value, ctx) => {
                                return !isEmpty(ctx.parent.phoneNumber);
                            },
                        })
                        .test({
                            message: "Phone Number is invalid",
                            test: (value, _ctx) => {
                                return isPhoneNumberPossible(value ?? "");
                            },
                        })
                        .label("Phone Number"),
                    phoneCountryCode: yup.string().required(),
                    phoneNumber: yup.string().required(),
                    addressStreet: yup
                        .string()
                        .required()
                        .test({
                            message: "Street address must be valid",
                            test: (value, _ctx) => !onlyContainsNumbers(value ?? ""),
                        })
                        .label("Street"),
                    addressStreet2: yup.string().optional(),
                    addressCity: yup
                        .string()
                        .required()
                        .test({
                            message: "City must be valid",
                            test: (value, _ctx) => !onlyContainsNumbers(value ?? ""),
                        })
                        .label("City"),
                    addressDistrict: yupValidateState("addressCountry").label("State / Province"),
                    addressZipcode: yupValidatePostalCode("addressCountry").label("ZIP Code"),
                    addressCountry: yup.string().nullable().required().label("Country"),
                })
                .defined(),
        )
        .test("max-100", "Total Ownership across all beneficiaries must be less than 100", (owners, context) => {
            if (!owners) return true;
            const ownership = sum(owners.map((b) => b.percentage || 0));
            if (ownership > 100) {
                return context.createError({
                    message: "Total ownership across all beneficiaries must be less than 100",
                    path: `beneficialOwner[${owners.length - 1}].percentage`,
                });
            }
            return true;
        })
        .defined(),
});

interface IProps {
    defaultValues?: IBeneficialOwnersFormData;
    officerData?: IOfficerInformationFormData;
    onSubmit: SubmitHandler<IBeneficialOwnersFormData>;
    prevPageButton?: ReactNode;
    nextPageButton?: ReactNode;
}

export const BeneficialOwnersForm: React.FC<IProps> = ({
    defaultValues,
    officerData,
    onSubmit,
    prevPageButton,
    nextPageButton,
}) => {
    const [isFirstOwnerAnOfficer, setIsFirstOwnerAnOfficer] = useState(
        defaultValues?.beneficialOwner[0]?.isOfficer ?? false,
    );
    const menuState = useMenuState();

    useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    }, []);

    const resolver = yupResolver(formSchema);
    const methods = useForm<IBeneficialOwnersFormData>({
        mode: "onTouched",
        resolver,
        defaultValues,
    });
    const { control, handleSubmit, watch } = methods;
    const { fields, append, prepend, remove } = useFieldArray({ name: "beneficialOwner", control });

    const handleAddBeneficialOwner = () => {
        append(NEW_BENEFICIAL_OWNER);
        Segment.track(EVENTS.APPLY_CLICK_ADD_BENEFICIAL_OWNER);
    };

    const handleRemoveBeneficialOwner = (i: number) => {
        return () => remove(i);
    };

    const handleAddOfficerAsBeneficialOwner = () => {
        prepend({ ...NEW_BENEFICIAL_OWNER, ...officerData, isOfficer: true });
        setIsFirstOwnerAnOfficer(true);
        Segment.track(EVENTS.APPLY_CLICK_ADD_BENEFICIAL_OWNER);
    };

    const handleRemoveOfficerAsBeneficialOwner = () => {
        remove(0);
        setIsFirstOwnerAnOfficer(false);
    };

    const [agreeToTerms1, agreeToTerms2] = [watch("agreeToTerms1"), watch("agreeToTerms2")];

    // Each beneficiary must have at least 25% ownership, so there can be no more than 4
    const beneficiaries = watch("beneficialOwner", []);
    const hasMaxBeneficiaries = beneficiaries.length >= 4;
    const addButtonText = fields.length ? "Add Additional Beneficial Owner" : "Add Beneficial Owner";

    return (
        <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)} onKeyDown={preventFormSubmitOnEnter}>
                <Typography>
                    Please fill out the following for all beneficial owners who hold 25% or more of your organization.
                </Typography>
                {fields.map((field, i) => {
                    const isOfficer = isFirstOwnerAnOfficer && i === 0 && field.email === officerData?.email;

                    return (
                        <Card key={field.id} variant="outlined" sx={{ my: 3 }}>
                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                bgcolor={(theme) => theme.palette.backgroundColors.cardTertiary.main}
                                p={1}
                            >
                                <Typography>
                                    {isOfficer ? `${field.firstName} ${field.lastName}` : `Beneficial Owner ${i + 1}`}
                                </Typography>
                                <IconButton
                                    size="small"
                                    onClick={
                                        isOfficer
                                            ? handleRemoveOfficerAsBeneficialOwner
                                            : handleRemoveBeneficialOwner(i)
                                    }
                                >
                                    <CloseIcon fontSize="inherit" />
                                </IconButton>
                            </Stack>
                            <CardContent>
                                {!isOfficer && (
                                    <>
                                        <Stack spacing={3} pb={3}>
                                            <FormTextInput
                                                label="First Name"
                                                name={`beneficialOwner.${i}.firstName`}
                                                required={true}
                                            />
                                            <FormTextInput
                                                label="Last Name"
                                                name={`beneficialOwner.${i}.lastName`}
                                                required={true}
                                            />
                                            <FormPhoneInput
                                                label="Phone Number"
                                                fullPhoneFieldName={`beneficialOwner.${i}.phoneNumberFull`}
                                                countryCodeFieldName={`beneficialOwner.${i}.phoneCountryCode`}
                                                localNumberFieldName={`beneficialOwner.${i}.phoneNumber`}
                                                required={true}
                                            />
                                            <FormTextInput
                                                label="Email"
                                                name={`beneficialOwner.${i}.email`}
                                                required={true}
                                            />
                                            <SsnOrPassportFieldGroup index={i} />
                                            <FormDatePicker
                                                name={`beneficialOwner.${i}.dob`}
                                                label="Date of Birth"
                                                required={true}
                                                disableFuture={true}
                                            />
                                            <FormSpreadAddressInput
                                                label="Home Address"
                                                description="Cannot be a PO box but international addresses are allowed"
                                                addressFields={{
                                                    street: `beneficialOwner.${i}.addressStreet`,
                                                    street2: `beneficialOwner.${i}.addressStreet2`,
                                                    city: `beneficialOwner.${i}.addressCity`,
                                                    state: `beneficialOwner.${i}.addressDistrict`,
                                                    zipCode: `beneficialOwner.${i}.addressZipcode`,
                                                    country: `beneficialOwner.${i}.addressCountry`,
                                                }}
                                            />
                                        </Stack>
                                    </>
                                )}
                                <FormTextInput
                                    label="Ownership Percentage"
                                    name={`beneficialOwner.${i}.percentage`}
                                    required={true}
                                    placeholder="25"
                                    endAdornment={<Typography color="text.secondary">%</Typography>}
                                />
                            </CardContent>
                        </Card>
                    );
                })}
                {!hasMaxBeneficiaries && (
                    <Stack direction="row" justifyContent="center" my={3}>
                        {officerData && !isFirstOwnerAnOfficer ? (
                            <>
                                <Button variant="contained" type="button" onClick={menuState.open}>
                                    {addButtonText}
                                </Button>
                                <Menu {...menuState.menuProps}>
                                    <MenuItem onClick={flow(handleAddOfficerAsBeneficialOwner, menuState.close)}>
                                        Add {officerData?.firstName} {officerData?.lastName}
                                    </MenuItem>
                                    <MenuItem onClick={flow(handleAddBeneficialOwner, menuState.close)}>
                                        Add New
                                    </MenuItem>
                                </Menu>
                            </>
                        ) : (
                            <Button variant="contained" type="button" onClick={handleAddBeneficialOwner}>
                                {addButtonText}
                            </Button>
                        )}
                    </Stack>
                )}
                <FormCheckbox
                    sx={{ mb: 3 }}
                    name="agreeToTerms1"
                    required={true}
                    label={
                        <Typography>
                            By providing the information above, I hereby certify, to the best of my knowledge, that the
                            information provided above is complete and correct.
                        </Typography>
                    }
                />
                <FormCheckbox
                    sx={{ mb: 3 }}
                    name="agreeToTerms2"
                    required={true}
                    label={
                        <Typography>
                            I also agree to the Levro{" "}
                            <Link href="https://www.levro.com/privacy" target="_blank" rel="noreferrer">
                                Privacy Policy
                            </Link>
                            ,{" "}
                            <Link href="https://www.levro.com/edocs" target="_blank" rel="noreferrer">
                                Consent to Electronic Disclosures
                            </Link>
                            ,{" "}
                            <Link
                                href="https://www.levro.com/business-deposit-account-agreement-thread"
                                target="_blank"
                                rel="noreferrer"
                            >
                                Levro Business Deposit Account Agreement
                            </Link>
                            ,{" "}
                            <Link href="https://www.levro.com/fx-tos" target="_blank" rel="noreferrer">
                                Foreign Exchange Services Addendum
                            </Link>
                            ,{" "}
                            <Link href="https://www.levro.com/tos" target="_blank" rel="noreferrer">
                                Client Terms of Service
                            </Link>
                            , and{" "}
                            <Link
                                href="https://www.levro.com/visa-cardholder-agreement-thread"
                                target="_blank"
                                rel="noreferrer"
                            >
                                Levro VISA Debit Cardholder Agreement
                            </Link>
                            .
                        </Typography>
                    }
                />
                <Stack direction="row" justifyContent="space-between" mt={3}>
                    <div>{prevPageButton}</div>
                    <div>
                        {agreeToTerms1 && agreeToTerms2
                            ? nextPageButton
                            : // eslint-disable-next-line @typescript-eslint/no-explicit-any
                              React.cloneElement(nextPageButton as any, { disabled: true })}
                    </div>
                </Stack>
            </form>
        </FormProvider>
    );
};

const SsnOrPassportFieldGroup: React.FC<{ index: number }> = ({ index: i }) => {
    const { resetField, watch } = useFormContext();

    const usingPassport = watch(`beneficialOwner.${i}.usePassport`);

    useEffect(() => {
        if (usingPassport) {
            resetField(`beneficialOwner.${i}.ssn`, { defaultValue: "" });
        } else {
            resetField(`beneficialOwner.${i}.passportNumber`, { defaultValue: "" });
            resetField(`beneficialOwner.${i}.nationality`, { defaultValue: "" });
        }
    }, [i, usingPassport, resetField]);

    return (
        <>
            <Grid container={true} spacing={4}>
                <Grid item={true} xs={6}>
                    <FormTextInput
                        label="SSN"
                        name={`beneficialOwner.${i}.ssn`}
                        required={!usingPassport}
                        disabled={usingPassport}
                    />
                </Grid>
                <Grid item={true} xs={6}>
                    <FormCheckbox
                        label="Does not have a SSN"
                        name={`beneficialOwner.${i}.usePassport`}
                        sx={{ mt: 2.3 }}
                    />
                </Grid>
            </Grid>
            {usingPassport && (
                <Grid container={true} spacing={4}>
                    <Grid item={true} xs={6}>
                        <FormTextInput
                            label="Passport #"
                            name={`beneficialOwner.${i}.passportNumber`}
                            required={true}
                        />
                    </Grid>
                    <Grid item={true} xs={6}>
                        <FormInputWrapper<IBeneficialOwnersFormData>
                            label="Passport Issuing Country"
                            name={`beneficialOwner.${i}.nationality`}
                            required={true}
                        >
                            <ControllerCountrySelect<IBeneficialOwnersFormData>
                                name={`beneficialOwner.${i}.nationality`}
                                controllerProps={{ rules: { required: true } }}
                                useCountryOptions={usePassportCountryOptions}
                                disableClearable={true}
                            />
                        </FormInputWrapper>
                    </Grid>
                </Grid>
            )}
        </>
    );
};
